import { RestrictToHorizontalAxis } from "@dnd-kit/abstract/modifiers";
import { DragDropProvider } from "@dnd-kit/react";
import { isSortable } from "@dnd-kit/react/sortable";

import { TooltipProvider } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import type { ImageStripItem } from "./ImageStripItem";
import { ImageStripTile } from "./ImageStripTile";
import { pendingUploadTiles, stripTiles } from "./imageStripLayout";
import { PendingUploadTile } from "./PendingUploadTile";
import { SortableImageStripTile } from "./SortableImageStripTile";

export type { ImageStripItem } from "./ImageStripItem";

type ImageStripProps<T extends ImageStripItem> = {
    images: T[];
    /** Hover actions + @dnd-kit drag to reorder. */
    editable?: boolean;
    emptyPlaceholderCount?: number;
    /** Images that should show a spinner while a slow action is in flight. */
    busyIds?: readonly string[];
    /** Skeleton tiles shown while new files are uploading and not yet in `images`. */
    pendingUploadCount?: number;
    onReorder?: (images: T[]) => void;
    onPlaceholderClick?: () => void;
    className?: string;
};

export function ImageStrip<T extends ImageStripItem = ImageStripItem>({
    images,
    editable = false,
    emptyPlaceholderCount = 0,
    busyIds = [],
    pendingUploadCount = 0,
    onReorder,
    onPlaceholderClick,
    className,
}: ImageStripProps<T>) {
    const tiles = stripTiles(images, emptyPlaceholderCount);
    const canSort = Boolean(editable && onReorder && images.length > 1);

    function moveItem(from: number, to: number) {
        if (from === to || from < 0 || to < 0 || from >= images.length) {
            return;
        }
        const next = images.slice();
        const [moved] = next.splice(from, 1);
        if (!moved) {
            return;
        }
        next.splice(to, 0, moved);
        onReorder?.(next);
    }

    function removeItem(index: number) {
        if (index < 0 || index >= images.length) {
            return;
        }
        onReorder?.(images.filter((_, itemIndex) => itemIndex !== index));
    }

    const strip = (
        <div className={cn("overflow-x-auto", className)}>
            <div className="flex snap-x snap-mandatory gap-2">
                {tiles.map((image, index) => {
                    const isCover = index === 0;
                    const showActions = Boolean(
                        editable && image.src && !busyIds.includes(image.id),
                    );
                    const busy = busyIds.includes(image.id);
                    const moveToFirst =
                        index === 0 ? undefined : () => moveItem(index, 0);

                    if (canSort && image.src) {
                        return (
                            <SortableImageStripTile
                                key={image.id}
                                image={image}
                                index={index}
                                isCover={isCover}
                                showActions={showActions}
                                busy={busy}
                                onMoveToFirst={moveToFirst}
                                onRemove={() => removeItem(index)}
                            />
                        );
                    }

                    return (
                        <ImageStripTile
                            key={image.id}
                            image={image}
                            isCover={isCover}
                            showActions={showActions}
                            busy={busy}
                            onPlaceholderClick={
                                image.src ? undefined : onPlaceholderClick
                            }
                            onMoveToFirst={moveToFirst}
                            onRemove={() => removeItem(index)}
                        />
                    );
                })}
                {pendingUploadTiles(pendingUploadCount).map((tile) => (
                    <PendingUploadTile
                        key={tile.key}
                        isCover={images.length === 0 && tile.isFirst}
                    />
                ))}
            </div>
        </div>
    );

    return (
        <TooltipProvider>
            {canSort ? (
                <DragDropProvider
                    modifiers={(defaults) => defaults.concat(RestrictToHorizontalAxis)}
                    onDragEnd={(event) => {
                        if (event.canceled) {
                            return;
                        }

                        const { source } = event.operation;

                        if (
                            isSortable(source) &&
                            source.initialIndex !== source.index
                        ) {
                            moveItem(source.initialIndex, source.index);
                        }
                    }}
                >
                    {strip}
                </DragDropProvider>
            ) : (
                strip
            )}
        </TooltipProvider>
    );
}
